import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import styles from "../EventDashboardV2.module.scss"
import StatCard from "./StatCard"
import type { InvitationStatusFilter, ThankStatusFilter } from "../constants/filters"
import {
  INVITATION_FILTERS,
  matchesInvitationFilter,
  matchesStatusWithFailureType,
} from "../constants/filters"
import { fetchAllEventContacts } from "../utils/fetchAllEventContacts"
export type { ThankStatusFilter }

interface ThankYouOverviewProps {
  invitationFilter: InvitationStatusFilter
  thankFilter: ThankStatusFilter
  onInvitationFilterChange: (filter: InvitationStatusFilter) => void
  onThankFilterChange: (filter: ThankStatusFilter) => void
}

interface ThankStats {
  sent: number
  notSent: number
  failedContact: number
  failedSystem: number
  total: number
}

const ThankYouOverview: React.FC<ThankYouOverviewProps> = ({
  invitationFilter,
  thankFilter,
  onInvitationFilterChange,
  onThankFilterChange,
}) => {
  const { t } = useTranslation()
  const { event_id } = useParams()
  const activeInv =
    INVITATION_FILTERS.find((f) => f.id === invitationFilter) ?? INVITATION_FILTERS[0]

  const [stats, setStats] = useState<ThankStats>({
    sent: 0,
    notSent: 0,
    failedContact: 0,
    failedSystem: 0,
    total: 0,
  })

  useEffect(() => {
    let cancelled = false

    const loadStats = async () => {
      try {
        const guests = await fetchAllEventContacts({
          sort: "",
          order: "",
          searchValue: "",
          id: Number(event_id),
          statusNumber: activeInv.statusNumber,
          waStatusNumber: activeInv.waStatusNumber,
          reminderNumber: "",
        })

        const scoped = guests.filter((g: any) => matchesInvitationFilter(g, activeInv))

        if (cancelled) return

        setStats({
          sent: scoped.filter((g: any) => g.thank_status === "sent").length,
          failedContact: scoped.filter((g: any) =>
            matchesStatusWithFailureType(
              g,
              "thank_status",
              "thank_failure_type",
              "failed_contact",
            ),
          ).length,
          failedSystem: scoped.filter((g: any) =>
            matchesStatusWithFailureType(
              g,
              "thank_status",
              "thank_failure_type",
              "failed_system",
            ),
          ).length,
          notSent: scoped.filter((g: any) =>
            matchesStatusWithFailureType(
              g,
              "thank_status",
              "thank_failure_type",
              "not_sent",
            ),
          ).length,
          total: scoped.length,
        })
      } catch {
        if (!cancelled) {
          setStats({
            sent: 0,
            notSent: 0,
            failedContact: 0,
            failedSystem: 0,
            total: 0,
          })
        }
      }
    }

    loadStats()
    return () => {
      cancelled = true
    }
  }, [event_id, activeInv])

  const { sent, notSent, failedContact, failedSystem, total } = stats
  const sentPercent = total > 0 ? Math.round((sent / total) * 100) : 0

  const selectFilter = (filter: ThankStatusFilter) => {
    const next = thankFilter === filter ? "all" : filter
    onThankFilterChange(next)
    onInvitationFilterChange("all")
  }

  return (
    <div>
      <h3 className={styles.sectionTitle}>{t("eventDashboardV2.labels.overview")}</h3>
      <div className={styles.overviewGrid}>
        <div className={`${styles.overviewRow} ${styles.overviewRowFour}`}>
          <StatCard
            value={sent}
            total={total}
            label={t("eventDashboardV2.filters.sent")}
            valueColor="Green"
            progress={sentPercent}
            onClick={() => selectFilter("sent")}
          />
          <StatCard
            value={notSent}
            total={total}
            label={t("eventDashboardV2.filters.not_sent")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => selectFilter("not_sent")}
          />
          <StatCard
            value={failedContact}
            total={total}
            label={t("eventDashboardV2.filters.failed_contact")}
            valueColor="Red"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => selectFilter("failed_contact")}
          />
          <StatCard
            value={failedSystem}
            total={total}
            label={t("eventDashboardV2.filters.failed_system")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => selectFilter("failed_system")}
          />
        </div>
      </div>
    </div>
  )
}

export default ThankYouOverview
